import { Box, Typography } from "@outschool/backpack";
import { CharterSchoolFragmentFragment } from "@outschool/gql-frontend-generated";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { useSession } from "@outschool/ui-auth";
import { Input } from "@outschool/ui-legacy-component-library";
import {
  FieldDefinition,
  isValidEmail,
  minLength,
  required,
  useFormState,
} from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";

import { PrepayType } from "../../../routes/EnrollmentMeetingPurchasePage";
import { EnrollmentMeetingPurchaseCharterInput } from "../EnrollmentMeetingPurchaseWithStripe";
import OrganizationList from "../OrganizationList";

const REQUIRED_VALIDATORS = [required(() => "Field is required")];
const PO_ID_MAX_LENGTH = 60;

const STRING_VALIDATORS = [
  ...REQUIRED_VALIDATORS,
  minLength(1, () => "Field is required"),
];

const EMAIL_VALIDATORS = [
  ...STRING_VALIDATORS,
  isValidEmail(() => "Field not a valid email"),
];

export default function CharterSchoolEnrollForm({
  setCharterEnrollmentInfo,
  disabled,
  prepayType,
}: {
  setCharterEnrollmentInfo: React.Dispatch<
    React.SetStateAction<EnrollmentMeetingPurchaseCharterInput | null>
  >;
  disabled?: boolean;
  prepayType?: PrepayType;
}) {
  const isPurchaseOrder = prepayType === PrepayType.CharterPO;
  const charterFormState = useFormState<{
    parentEmail: string;
    parentName: string;
    charterSchool: CharterSchoolFragmentFragment;
    intercomUrl: string;
    purchaseOrderId: string;
  }>({
    parentEmail: {
      value: "",
      validators: EMAIL_VALIDATORS,
    },
    parentName: {
      value: "",
      validators: STRING_VALIDATORS,
    },
    // @ts-ignore TS(2352): Conversion of type '{ value: null; validators: ((v... Remove this comment to see the full error message
    charterSchool: {
      value: null,
      validators: isPurchaseOrder ? REQUIRED_VALIDATORS : [],
    } as FieldDefinition<CharterSchoolFragmentFragment>,
    intercomUrl: {
      value: "",
      validators: isPurchaseOrder ? STRING_VALIDATORS : [],
    },
    purchaseOrderId: {
      value: "",
      validators: isPurchaseOrder ? STRING_VALIDATORS : [],
    },
  });
  const { fields, errors, setters, values } = charterFormState;
  const { charterSchool } = values;
  const { currentUser } = useSession();
  const organizationName =
    charterSchool?.name ||
    currentUser?.organizationAdmin?.organization?.name ||
    "Admin";

  const hasErrors = !_.isEmpty(
    // @ts-ignore TS(2531): Object is possibly 'null'.
    _.pickBy(charterFormState.errors, e => e.length > 0),
  );

  React.useEffect(() => {
    const name = charterFormState.values.parentName?.trim();
    const email = charterFormState.values.parentEmail?.trim();
    const purchaseOrderId = charterFormState.values.purchaseOrderId?.trim();
    const intercomUrl = charterFormState.values.intercomUrl?.trim();
    const charterSchool = charterFormState.values.charterSchool;

    const hasEmailAndName = Boolean(name?.length) && Boolean(email?.length);

    const hasRequiredFields = isPurchaseOrder
      ? !hasErrors &&
        hasEmailAndName &&
        Boolean(purchaseOrderId?.length) &&
        Boolean(intercomUrl?.length) &&
        Boolean(charterSchool)
      : !hasErrors && hasEmailAndName;

    const charterInfo: EnrollmentMeetingPurchaseCharterInput = isPurchaseOrder
      ? {
          name,
          email,
          purchaseOrderId,
          intercomUrl,
          charterSchool,
          isValid: hasRequiredFields,
        }
      : {
          name,
          email,
          isValid: hasRequiredFields,
        };
    setCharterEnrollmentInfo(charterInfo);
  }, [
    isPurchaseOrder,
    charterFormState.values,
    hasErrors,
    setCharterEnrollmentInfo,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "space-between",
        marginBottom: "1em",
      }}
    >
      <>
        <Typography variant="h3">{organizationName} Enrollment Form</Typography>
        {isPurchaseOrder && (
          <OrganizationList
            // @ts-ignore TS(2322): Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message
            disabled={disabled}
            onSelect={charterSchool => {
              setters.charterSchool(charterSchool);
            }}
          />
        )}
        <Box
          sx={{
            marginBottom: "0.5em",
          }}
        >
          <Input
            type="email"
            disabled={disabled}
            name="parentEmail"
            sx={{
              width: "100%",
              boxSizing: "border-box",
              borderColor:
                // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
                Boolean(errors.parentEmail.length) ? "red" : undefined,
            }}
            placeholder="Parent Email"
            {...fields.parentEmail}
          />
          {/* @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'. */}
          {Boolean(errors.parentEmail.length) && (
            <Box
              sx={{
                marginLeft: "1em",
                color: "red",
              }}
            >
              Please provide parent email
            </Box>
          )}
        </Box>
        <Box
          sx={{
            marginBottom: "0.5em",
          }}
        >
          <Input
            type="text"
            disabled={disabled}
            name="parentName"
            sx={{
              width: "100%",
              boxSizing: "border-box",
              borderColor:
                // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
                Boolean(errors.parentName.length) ? "red" : undefined,
            }}
            placeholder="Parent Name"
            {...fields.parentName}
          />
          {/* @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'. */}
          {Boolean(errors.parentName.length) && (
            <Box
              sx={{
                marginLeft: "1em",
                color: "red",
              }}
            >
              Please provide parent name
            </Box>
          )}
        </Box>
        {isPurchaseOrder && (
          <>
            <Box
              sx={{
                marginBottom: "0.5em",
              }}
            >
              <Input
                type="text"
                maxLength={PO_ID_MAX_LENGTH}
                disabled={disabled}
                name="purchaseOrderId"
                sx={{
                  width: "100%",
                  boxSizing: "border-box",
                  borderColor:
                    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
                    charterSchool && Boolean(errors.purchaseOrderId.length)
                      ? "red"
                      : undefined,
                }}
                placeholder="Purchase Order Id"
                {...fields.purchaseOrderId}
              />
              {/* @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'. */}
              {Boolean(errors.purchaseOrderId.length) && (
                <Box
                  sx={{
                    marginLeft: "1em",
                    color: "red",
                  }}
                >
                  Please provide a purchase order id
                </Box>
              )}
            </Box>
            <Box
              sx={{
                marginBottom: "0.5em",
              }}
            >
              <Input
                type="text"
                disabled={disabled}
                name="intercomUrl"
                sx={{
                  width: "100%",
                  boxSizing: "border-box",
                  borderColor:
                    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
                    charterSchool && Boolean(errors.intercomUrl.length)
                      ? "red"
                      : undefined,
                }}
                placeholder="Intercom URL"
                {...fields.intercomUrl}
              />
              {/* @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'. */}
              {Boolean(errors.intercomUrl.length) && (
                <Box
                  sx={{
                    marginLeft: "1em",
                    color: "red",
                  }}
                >
                  Please provide an intercom url
                </Box>
              )}
            </Box>
          </>
        )}
      </>
    </Box>
  );
}
